/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box, Button, Drawer, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import SearchBar from "../../components/SearchBar";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AddIcon from "@mui/icons-material/Add";
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import ConfigurePricing from "../../Modals/ConfigurePricing";

type Priority = "HIGH" | "MEDIUM" | "LOW";

interface Card {
    id: number;
    title: string;
    priority: Priority;
    description: string;
    hint: string;
    price: string;
    usage: string;
    visibility: string;
    active?: string;
}

const AIHub = () => {

    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [pricingModalOpen, setPricingModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<Card | null>(null);

    const cards: Card[] = [
        {
            id: 1,
            title: "AI Seasonal Opportunity Predictor",
            priority: "HIGH",
            description:
                "Predict upcoming seasonal demand fluctuations. Your store can gain early advantage by optimizing listings and running targeted offers.",
            hint: "Prepare listings and campaigns for predicted seasonal demand.",
            price: "AED 99",
            usage: "3 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
            active: "1 Sep 2024 – 30 Sep 2024",
        },
        {
            id: 2,
            title: "AI Auto-Optimization Engine",
            priority: "HIGH",
            description:
                "Your product performance can be improved by automated adjustments to pricing, tags, SEO, and placement.",
            hint: "Enable automated optimization rules to boost performance.",
            price: "AED 99",
            usage: "10 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 4,
            title: "AI Listing Quality Improvement",
            priority: "MEDIUM",
            description:
                "Several products may have missing or weak titles, descriptions, or keywords.",
            hint: "Best suited for Grow & Scale tiers.",
            price: "AED 49",
            usage: "3 runs/month",
            visibility: "Scale, Pro, Elite",
        },
        {
            id: 5,
            title: "AI Conversion Funnel Diagnosis",
            priority: "HIGH",
            description:
                "AI identified drop-offs in your product -> cart -> checkout funnel.",
            hint: "Fix bottlenecks to improve conversion rate.",
            price: "AED 129",
            usage: "5 runs/month",
            visibility: "Scale, Pro, Elite",
        },
        {
            id: 6,
            title: "AI Product Ranking Booster",
            priority: "HIGH",
            description:
                "Ranking fluctuations detected due to low engagement and fewer recent updates.",
            hint: "Refresh listings to increase ranking.",
            price: "AED 79",
            usage: "5 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 7,
            title: "AI High-Potential Product Detector",
            priority: "HIGH",
            description:
                "Some products show a steep increase in impressions but low conversions.",
            hint: "Review pricing and content for high-potential items.",
            price: "AED 89",
            usage: "5 runs/month",
            visibility: "Scale, Pro, Elite",
        },
        {
            id: 8,
            title: "AI Geo Expansion Suggestion",
            priority: "LOW",
            description:
                "Your products have emerging demand in new regions or countries.",
            hint: "Enable multi-region listing to expand visibility.",
            price: "AED 149",
            usage: "2 runs/month",
            visibility: "Pro, Elite",
        },
        {
            id: 9,
            title: "AI Demand Surge & Trend Alert",
            priority: "HIGH",
            description:
                "Spike in searches and interest for your category detected.",
            hint: "Stock up and adjust pricing for trending items.",
            price: "AED 119",
            usage: "5 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 10,
            title: "AI Slow-Moving Inventory Solver",
            priority: "MEDIUM",
            description:
                "Several products have minimal sales over the last 30 days.",
            hint: "Run discount or bundle offers to clear stock.",
            price: "AED 69",
            usage: "10 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 11,
            title: "AI Returns & Cancellation Cause Predictor",
            priority: "LOW",
            description:
                "AI detected patterns in returns that can be prevented by improving clarity.",
            hint: "Fix misleading details to reduce returns.",
            price: "AED 159",
            usage: "3 runs/month",
            visibility: "Scale, Pro, Elite",
        },
        {
            id: 12,
            title: "AI VIP & Repeat Customer Win-back",
            priority: "HIGH",
            description:
                "Some loyal customers haven't returned recently.",
            hint: "Send targeted offers to win them back.",
            price: "AED 99",
            usage: "5 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 13,
            title: "AI Marketing Best Timing",
            priority: "MEDIUM",
            description:
                "AI identified the best times your customers are active.",
            hint: "Schedule posts and discounts at optimal times.",
            price: "AED 49",
            usage: "10 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 14,
            title: "AI Competitive Benchmarking",
            priority: "HIGH",
            description:
                "Competitors changed pricing and promotions.",
            hint: "Adjust strategy to stay competitive.",
            price: "AED 139",
            usage: "5 runs/month",
            visibility: "Scale, Pro, Elite",
        },
        {
            id: 15,
            title: "AI Keyword & SEO Opportunity Finder",
            priority: "MEDIUM",
            description:
                "New trending keywords have emerged for your category.",
            hint: "Update SEO metadata to gain traffic.",
            price: "AED 59",
            usage: "10 runs/month",
            visibility: "Grow, Scale, Pro, Elite",
        },
        {
            id: 16,
            title: "AI Competitor Behavior & Pricing Shift Radar",
            priority: "HIGH",
            description:
                "Competitors are rapidly adjusting prices or stock.",
            hint: "Monitor competition and update strategy.",
            price: "AED 169",
            usage: "3 runs/month",
            visibility: "Pro, Elite",
        },
    ];

    return (
        <>
            <div className="main_title">
                <h1>AI Insights</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - AI Insights
                </p>
            </div>

            <div className="stats_boxes gap_m">
                <a className="cards w_33">
                    <div className="data_top">
                        <h2>
                            16
                            <p>Total Insights</p>
                        </h2>
                        <figure className="blue">
                            <LightbulbOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>
                <a className="cards w_33">
                    <div className="data_top">
                        <h2>
                            3
                            <p>Limited-Time Campaigns</p>
                        </h2>
                        <figure className="warning">
                            <LightbulbOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>
                <a className="cards w_33">
                    <div className="data_top">
                        <h2>
                            AED 17,950
                            <p>Monthly Potential Revenue</p>
                        </h2>
                        <figure className="sucess">
                            <AttachMoneyOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                </a>
            </div>

            <div className="cards mt_20">
                <div className="table_header">
                    <div className="left_s">
                        <Box className="search_box">
                            <SearchBar
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setSearchTerm}
                                placeholder="Search..."
                            />
                        </Box>
                    </div>
                </div>
                <div className="ai-automation-wrapper gap_m">
                    {cards.map((card) => (
                        <div key={card.id} className="ai-card">
                            {/* Header */}
                            <div className="ai-card__header">
                                <h3>
                                    <strong>{card.title}</strong>
                                    <span className={`priority ${card.priority.toLowerCase()}`}>
                                        {card.priority}
                                    </span>
                                </h3>
                                <p>{card.description}</p>
                            </div>

                            {/* Hint */}
                            <div className="ai-card__hint">
                                <i>‹</i> {card.hint}
                            </div>

                            {/* Admin Config */}
                            <h4>
                                Admin Configurations:
                                <button onClick={() => { setDrawerOpen(true); }}><LightbulbOutlinedIcon /> Get AI Pricing Recommendation</button>
                            </h4>

                            <div className="ai-card__config">
                                <div>
                                    <b>Price:</b> <span className="c_success">{card.price}</span>
                                </div>
                                <div>
                                    <b>Usage Limit:</b> {card.usage}
                                </div>

                                {card.active && (
                                    <div>
                                        <b>Active:</b> {card.active}
                                    </div>
                                )}

                                <div>
                                    <b>Visibility:</b> {card.visibility}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="ai-card__actions">
                                <Button
                                    className="btn btn_primary"
                                    onClick={() => {
                                        setSelectedCard(card);
                                        setPricingModalOpen(true);
                                    }}
                                >
                                    Configure Pricing
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div >

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                className="ai_insight_drawer"
            >
                <Box className="ai_drawer">
                    {/* Header */}
                    <div className="ai_drawer_header">
                        <h3>
                            <LightbulbOutlinedIcon />
                            Get AI Pricing Recommendation
                        </h3>
                        <IconButton onClick={() => setDrawerOpen(false)}><AddIcon sx={{ transform: 'rotate(45deg)' }} /></IconButton>
                    </div>

                    {/* Description */}
                    <p className="ai_drawer_desc">
                        Based on Bouteek platform data and cost estimates, here is the
                        AI-recommended pricing and usage configuration for
                        <b> AI Seasonal Opportunity Predictor</b>.
                        <br />
                        You can adjust the price and usage limits based on these suggestions.
                        Final configuration is fully controlled by Admin.
                    </p>

                    {/* Recommended Price */}
                    <div className="ai_drawer_section">
                        <h4>AI-Recommended Price</h4>
                        <div className="price_box">
                            <LightbulbOutlinedIcon />
                            <b>AED 99</b>
                            <span>/ one-time buy or per month</span>
                        </div>
                    </div>

                    {/* Usage Limit */}
                    <div className="ai_drawer_section">
                        <h4>AI-Recommended Usage Limit</h4>
                        <div className="usage_box"><b>3 runs</b> per month</div>
                    </div>

                    {/* Reasoning */}
                    <div className="ai_drawer_section">
                        <h4>Reasoning Behind Suggestion:</h4>
                        <ul className="reason_list">
                            <li><DoneAllOutlinedIcon /> High algorithm cost</li>
                            <li><DoneAllOutlinedIcon /> High conversion impact for seasonal sales</li>
                            <li><DoneAllOutlinedIcon /> Best suited for Grow & Scale tiers or higher</li>
                        </ul>
                    </div>

                    {/* Profitability */}
                    <div className="ai_drawer_section profitability">
                        <h4>Profitability Score: <span>High</span></h4>

                        <div className="profit_item">
                            <span className="dot free"></span>
                            <p>
                                <b>Free Trial:</b> <span>AED 0</span> – 2 runs within 7 days
                            </p>
                        </div>

                        <div className="profit_item">
                            <span className="dot paid"></span>
                            <p>
                                <b>Limited-Time Campaign:</b> <span>AED 69</span> – 2 runs within 14 days
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="ai_drawer_actions">
                        <Button className="btn btn_primary">Apply Suggestions</Button>
                        <Button className="btn btn_primary br">Adjust Manually</Button>
                        <Button className="btn btn_grey" onClick={() => setDrawerOpen(false)}>
                            Cancel
                        </Button>
                    </div>
                </Box>
            </Drawer>

            <ConfigurePricing
                open={pricingModalOpen}
                onClose={() => setPricingModalOpen(false)}
                setOpen={setPricingModalOpen}
                title={selectedCard?.title || ""}
            />
        </>
    );
};

export default AIHub;
